import * as Sentry from "@sentry/react";
import { AnyAction, Reducer } from "redux";
import { IAppThunkAction } from ".";
import ParcelApi from "../api/ParcelApi";
import { parseAuctionInstant } from "../helpers/auctionDateTime";
import { mergeById } from "../helpers/mergeById";
import { RoleParcelState } from "../models/auction/RoleParcelState";
import { ICurrentBid } from "../models/parcel/ICurrentBid";
import {
    IParcelBid,
    IParcelBidDict,
    IParcelBidRequest
} from "../models/parcel/IParcelBid";
import {
    IParcelBidLog,
    IParcelBidLogState
} from "../models/parcel/IParcelBidLog";
import Constants from "../models/utility/Constants";
import {
    BidLoadState,
    isCriticalErrorBidLoadState,
    LoadState
} from "../models/utility/LoadState";
import type { ILogoutUser } from "./UserStore";

export interface IParcelBidStoreState {
    bidDialogVisible: boolean;
    currentBid: ICurrentBid;
    parcelAdminBidLogs: IParcelBidLogState[];
    parcelBidDict: IParcelBidDict;
    parcelBidLoadState: LoadState;
    myBidIds: string[];
}

export const isMyBid = (myBidIds: string[], bid?: IParcelBid | null) =>
    !!bid && myBidIds.includes(bid.parcelBidId);

export const holdsHighBid = (myBidIds: string[], bids?: IParcelBid[]) =>
    isMyBid(myBidIds, bids?.[bids.length - 1]);

// Returns myBidIds itself when nothing is new, so subscribers don't re-render.
const addMyBidIds = (myBidIds: string[], bidIds: string[]) => {
    const newIds = bidIds.filter((id) => !myBidIds.includes(id));

    return newIds.length > 0 ? [...myBidIds, ...newIds] : myBidIds;
};

const addMyBidsFrom = (myBidIds: string[], bids: IParcelBid[]) =>
    addMyBidIds(
        myBidIds,
        bids.filter((bid) => bid.isMine).map((bid) => bid.parcelBidId)
    );

const getTimestamp = (bidReceivedTime: string) => {
    const receivedTime = parseAuctionInstant(bidReceivedTime);

    if (receivedTime) {
        return receivedTime.epochMilliseconds;
    }

    // Bid received timestamps should always be valid ISO strings from the API.
    Sentry.captureMessage("Invalid parcel bid received timestamp", {
        extra: { bidReceivedTime }
    });

    return 0;
};

const sortParcelBidLogs = (logs: IParcelBidLog[]) => {
    const timestamps = new Map(
        logs.map((log) => [log, getTimestamp(log.bidReceivedTime)])
    );

    return [...logs].sort((a, b) => {
        const receivedTimeComparison =
            (timestamps.get(b) ?? 0) - (timestamps.get(a) ?? 0);

        if (receivedTimeComparison !== 0) {
            return receivedTimeComparison;
        }

        // When timestamps match, sort by ID only to keep the row order consistent.
        return b.parcelBidId.localeCompare(a.parcelBidId);
    });
};

const mergeParcelBidLogs = (
    existingLogs: IParcelBidLog[],
    incomingLogs: IParcelBidLog[]
) =>
    sortParcelBidLogs(
        mergeById(existingLogs, incomingLogs, (log) => log.parcelBidId)
    );

export type KnownAction =
    | IAddMyBidId
    | IAddParcelAdminBidLog
    | IAddParcelBid
    | ILogoutUser
    | IToggleBidDialogVisible
    | IToggleBidLoadState
    | IToggleParcelAdminBidLogsLoadState
    | IToggleParcelBidsDictLoadState
    | IUpdateLiveParcelBids
    | IUpdateAllParcelBids
    | IUpdateCurrentBid
    | IUpdateParcelAdminBidLogs
    | ITrackNewBid;

export interface IAddMyBidId {
    type: "ADD_MY_BID_ID";
    bidId: string;
}
export interface IAddParcelAdminBidLog {
    type: "ADD_PARCEL_ADMIN_BID_LOG";
    bidLog: IParcelBidLog;
    parcelId: string;
}
export interface IAddParcelBid {
    type: "ADD_PARCEL_BID";
    bid: IParcelBid;
}
export interface IToggleBidDialogVisible {
    type: "TOGGLE_BID_DIALOG_VISIBLE";
    visible: boolean;
}
export interface IToggleBidLoadState {
    type: "TOGGLE_BID_LOAD_STATE";
    errorText: string;
    state: BidLoadState;
}
export interface IToggleParcelAdminBidLogsLoadState {
    type: "TOGGLE_PARCEL_ADMIN_BID_LOGS_LOAD_STATE";
    parcelId: string;
    loadState: LoadState;
}
export interface IToggleParcelBidsDictLoadState {
    type: "TOGGLE_PARCEL_BIDS_DICT_LOAD_STATE";
    state: LoadState;
}
export interface IUpdateAllParcelBids {
    type: "UPDATE_ALL_PARCEL_BIDS";
    bids: IParcelBidDict;
}
export interface IUpdateLiveParcelBids {
    type: "UPDATE_LIVE_PARCEL_BIDS";
    parcelState: RoleParcelState;
}
export interface IUpdateCurrentBid {
    type: "UPDATE_CURRENT_BID";
    currentBid: ICurrentBid;
}
export interface IUpdateParcelAdminBidLogs {
    type: "UPDATE_PARCEL_ADMIN_BID_LOGS";
    parcelId: string;
    logs: IParcelBidLog[];
}
export interface ITrackNewBid {
    type: "TRACK_NEW_BID_ID";
    bidId: string;
}

// The current bid once the dialog closes. It has no `bidId`, so the poll
// fallback waits for TRACK_NEW_BID_ID (#1195).
export const EMPTY_CURRENT_BID = {
    parcelAmount: 0,
    parcelId: "",
    parcelName: "",
    parcelToken: "",
    submitErrorText: "",
    submitLoadState: BidLoadState.NotSubmitted
} as ICurrentBid;

export const actionCreators = {
    loadParcelAdminBidLogs:
        (parcelId: string): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "TOGGLE_PARCEL_ADMIN_BID_LOGS_LOAD_STATE",
                parcelId,
                loadState: LoadState.Loading
            });

            const logs = await ParcelApi.GetParcelBidLogs(parcelId);
            dispatch({ type: "UPDATE_PARCEL_ADMIN_BID_LOGS", parcelId, logs });
        },
    loadParcelBidsForAuction:
        (auctionId: string): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "TOGGLE_PARCEL_BIDS_DICT_LOAD_STATE",
                state: LoadState.Loading
            });

            const bids = await ParcelApi.GetParcelBids(auctionId);

            dispatch({
                type: "TOGGLE_PARCEL_BIDS_DICT_LOAD_STATE",
                state: LoadState.Loaded
            });
            dispatch({ type: "UPDATE_ALL_PARCEL_BIDS", bids });
        },
    resetCurrentBid: (): IAppThunkAction => (dispatch) => {
        dispatch({ type: "UPDATE_CURRENT_BID", currentBid: EMPTY_CURRENT_BID });
    },
    submitBid: (): IAppThunkAction => async (dispatch, getState) => {
        dispatch({
            type: "TOGGLE_BID_LOAD_STATE",
            errorText: "",
            state: BidLoadState.Submitting
        });

        const currentBid = getState().parcelBidStoreState.currentBid;
        const request: IParcelBidRequest = {
            checkoutToken: currentBid.parcelToken,
            parcelId: currentBid.parcelId,
            bidAmount: currentBid.parcelAmount
        };

        try {
            const response = await ParcelApi.SubmitParcelBid(request);

            if (response && response.errors && response.errors.length > 0) {
                const errorMsg =
                    "Errors detected while submitting the bid: " +
                    response.errors.map((err) => err.description).join(", ");
                Sentry.captureException(new Error(errorMsg), {
                    extra: { response }
                });
                dispatch({
                    type: "TOGGLE_BID_LOAD_STATE",
                    errorText: errorMsg,
                    state: BidLoadState.ErrorOther
                });

                return;
            }

            // Handle the case the bid was returned in a critical error state that we don't expect to occur
            if (
                response &&
                response.bidResponse &&
                isCriticalErrorBidLoadState(response.bidResponse.result)
            ) {
                const errorMsg =
                    BidLoadState[response.bidResponse.result] +
                    ": " +
                    response.bidResponse.errorText;
                Sentry.captureException(new Error(errorMsg), {
                    extra: { response }
                });
                dispatch({
                    type: "TOGGLE_BID_LOAD_STATE",
                    errorText: errorMsg,
                    state: response.bidResponse.result
                });

                return;
            }

            if (response && response.bidResponse) {
                dispatch({
                    type: "TRACK_NEW_BID_ID",
                    bidId: response.bidResponse.bidId
                });
            }
        } catch (e) {
            if (e instanceof Error) {
                const errorText = e.message;
                if (errorText.indexOf("unauthorized") >= 0) {
                    dispatch({
                        type: "TOGGLE_BID_LOAD_STATE",
                        errorText: "Oops! Your login has expired.",
                        state: BidLoadState.ErrorLogin
                    });
                    dispatch({ type: "LOGOUT_USER" });

                    return;
                }

                Sentry.captureException(e);
                dispatch({
                    type: "TOGGLE_BID_LOAD_STATE",
                    errorText,
                    state: BidLoadState.ErrorOther
                });
            }
        }
    },
    toggleDialogVisible:
        (visible: boolean): IAppThunkAction =>
        (dispatch) => {
            dispatch({ type: "TOGGLE_BID_DIALOG_VISIBLE", visible });
        },
    toggleParcelAdminBidLogLoadState:
        (parcelId: string, loadState: LoadState): IAppThunkAction =>
        (dispatch) => {
            dispatch({
                type: "TOGGLE_PARCEL_ADMIN_BID_LOGS_LOAD_STATE",
                parcelId,
                loadState
            });
        },
    updateCurrentBid:
        (
            parcelId: string,
            state: BidLoadState,
            checkoutToken = "",
            lastAcceptedBid: number | undefined = undefined,
            errorText = ""
        ): IAppThunkAction =>
        (dispatch, getState) => {
            const parcel = getState().parcelStoreState.parcels.find(
                (e) => e.parcelId === parcelId
            );
            const parcelBids =
                getState().parcelBidStoreState.parcelBidDict[parcelId] || [];

            if (!parcel) {
                return;
            }

            // ensure that initial first bid is not incremented
            const bidIncrement =
                parcelBids && parcelBids.length > 0
                    ? parcel.bidIncrement || Constants.DefaultBidIncrement
                    : 0;

            const prevAmount =
                parcel.lastAcceptedBid || parcel.startingBid || 0;
            const prevToken = parcel.checkOutToken;

            const currentBid = {
                parcelAmount: (lastAcceptedBid || prevAmount) + bidIncrement,
                parcelId: parcel.parcelId,
                parcelName: parcel.name,
                parcelToken: checkoutToken || prevToken,
                submitErrorText: errorText,
                submitLoadState: state
            } as ICurrentBid;

            dispatch({ type: "UPDATE_CURRENT_BID", currentBid });
        },
    toggleBidLoadState:
        (state: BidLoadState, errorText: string): IAppThunkAction =>
        (dispatch) => {
            dispatch({
                type: "TOGGLE_BID_LOAD_STATE",
                state: state,
                errorText: errorText
            });
        }
};

export const reducer: Reducer<IParcelBidStoreState> = (
    state: IParcelBidStoreState | undefined,
    incomingAction: AnyAction
) => {
    const action = incomingAction as KnownAction;
    const prevState = state || defaultState;

    switch (action.type) {
        case "ADD_MY_BID_ID": {
            return {
                ...prevState,
                myBidIds: addMyBidIds(prevState.myBidIds, [action.bidId])
            };
        }
        case "LOGOUT_USER": {
            return {
                ...prevState,
                myBidIds: []
            };
        }
        case "ADD_PARCEL_ADMIN_BID_LOG": {
            const parcelAdminBidLogs = prevState.parcelAdminBidLogs.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    logs: mergeParcelBidLogs(e.logs, [action.bidLog])
                };
            });

            return {
                ...prevState,
                parcelAdminBidLogs
            };
        }
        case "ADD_PARCEL_BID": {
            const parcelBidDict = {
                ...prevState.parcelBidDict
            };

            const arr = parcelBidDict[action.bid.parcelId] || [];
            parcelBidDict[action.bid.parcelId] = mergeById(
                arr,
                [action.bid],
                (bid) => bid.parcelBidId
            );

            return {
                ...prevState,
                parcelBidDict
            };
        }
        case "TOGGLE_BID_DIALOG_VISIBLE": {
            return {
                ...prevState,
                bidDialogVisible: action.visible
            };
        }
        case "TOGGLE_BID_LOAD_STATE": {
            return {
                ...prevState,
                currentBid: {
                    ...prevState.currentBid,
                    submitErrorText: action.errorText,
                    submitLoadState: action.state
                }
            };
        }
        case "TRACK_NEW_BID_ID": {
            return {
                ...prevState,
                currentBid: {
                    ...prevState.currentBid,
                    bidId: action.bidId
                }
            };
        }
        case "TOGGLE_PARCEL_ADMIN_BID_LOGS_LOAD_STATE": {
            let parcelAdminBidLogs = [...prevState.parcelAdminBidLogs];

            const oldStateIdx = prevState.parcelAdminBidLogs.findIndex(
                (e) => e.parcelId === action.parcelId
            );
            const newState = {
                loadState: action.loadState,
                logs: [],
                parcelId: action.parcelId
            } as IParcelBidLogState;

            if (oldStateIdx > -1) {
                newState.logs = [
                    ...prevState.parcelAdminBidLogs[oldStateIdx].logs
                ];

                parcelAdminBidLogs = prevState.parcelAdminBidLogs.map((e) => {
                    if (e.parcelId !== action.parcelId) {
                        return e;
                    }

                    return newState;
                });
            } else {
                parcelAdminBidLogs.push(newState);
            }

            return {
                ...prevState,
                parcelAdminBidLogs
            };
        }
        case "TOGGLE_PARCEL_BIDS_DICT_LOAD_STATE": {
            return {
                ...prevState,
                parcelBidLoadState: action.state
            };
        }
        case "UPDATE_ALL_PARCEL_BIDS": {
            return {
                ...prevState,
                parcelBidDict: action.bids,
                myBidIds: addMyBidsFrom(
                    prevState.myBidIds,
                    Object.values(action.bids).flat()
                )
            };
        }
        case "UPDATE_LIVE_PARCEL_BIDS": {
            const parcelBidDict = {
                ...prevState.parcelBidDict
            };

            const currentParcelBids =
                prevState.parcelBidDict &&
                prevState.parcelBidDict[action.parcelState.parcelId]
                    ? prevState.parcelBidDict[action.parcelState.parcelId]
                    : [];
            const responseParcelBids =
                action.parcelState.bids[action.parcelState.parcelId] ?? [];
            const latestParcelBids =
                currentParcelBids &&
                responseParcelBids &&
                currentParcelBids.length !== responseParcelBids.length
                    ? responseParcelBids
                    : currentParcelBids;

            parcelBidDict[action.parcelState.parcelId] = [...latestParcelBids];

            const parcelAdminBidLogs = prevState.parcelAdminBidLogs.map((e) => {
                if (e.parcelId !== action.parcelState.parcelId) {
                    return e;
                }

                return {
                    loadState: LoadState.Loaded,
                    logs: mergeParcelBidLogs(
                        [],
                        "bidLogs" in action.parcelState
                            ? action.parcelState.bidLogs
                            : []
                    ),
                    parcelId: action.parcelState.parcelId
                } as IParcelBidLogState;
            });

            return {
                ...prevState,
                parcelAdminBidLogs,
                parcelBidDict,
                myBidIds: addMyBidsFrom(prevState.myBidIds, responseParcelBids)
            };
        }
        case "UPDATE_CURRENT_BID": {
            return {
                ...prevState,
                currentBid: action.currentBid
            };
        }
        case "UPDATE_PARCEL_ADMIN_BID_LOGS": {
            // update state in array
            const parcelAdminBidLogs = prevState.parcelAdminBidLogs.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    loadState: LoadState.Loaded,
                    logs: mergeParcelBidLogs([], action.logs),
                    parcelId: action.parcelId
                } as IParcelBidLogState;
            });

            return {
                ...prevState,
                parcelAdminBidLogs
            };
        }
        default:
            break;
    }

    return prevState;
};

export const defaultState = {
    bidDialogVisible: false,
    currentBid: {} as ICurrentBid,
    parcelAdminBidLogs: [],
    parcelBidDict: {},
    parcelBidLoadState: LoadState.NotLoaded,
    myBidIds: []
} as IParcelBidStoreState;
